import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { Button, ButtonGroup, Collapse } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatThousands, formatCurrency } from "../../../../_helpers/numberFormatter";
import classNames from "classnames";
import "./projAdminItems.css";
import "../styles/projAdmin.css";
import EditDivOfWorkModalV3 from "./EditDivOfWorkModalV3";
import { docPermConstants } from "../../../../_constants/permissionConstants";
import bidItemActions from "../../../../_actions/bidItemActions";
import EditItemModalV2 from "./EditItemModalV2";
import locationActions from "../../../../_actions/locationActions";

export default function ProjAdminItemsListV2(props) {
  const { projectId, permissions } = props;

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(locationActions.getAll(projectId));
    dispatch(bidItemActions.getAll(projectId));
  }, []);

  const openBidItemDetails = useSelector(
    (state) => state.bidItems.openBidItemDetails[projectId] || null
  );
  const projItemList = useSelector(
    (state) => state.bidItems.projItemLists[projectId] || null
  );
  const projDetails = useSelector(
    (state) => state.projects.openProjectDetails[projectId] || null
  );
  const fetching = useSelector((state) => state.bidItems.fetching || false);

  const [primaryFilter, setPrimaryFilter] = useState("filterAll");
  const [descFilter, setDescFilter] = useState("");

  const [expandedItems, setExpandedItems] = useState({});
  useEffect(() => {
    const _expandedItems = { ...expandedItems };
    (projItemList || []).forEach(
      (i) => (_expandedItems[i.bidItemId] = _expandedItems[i.bidItemId] || false)
    );
    setExpandedItems(_expandedItems);
  }, [projItemList]);

  const [sortColumn, setSortColumn] = useState({ path: "itemNo", order: "asc" });
  const selectedBidItemId = useRef(0);
  const setSelectedBidItemId = useCallback(
    (bidItemId) => (selectedBidItemId.current = bidItemId),
    []
  );

  const [modalData_editItem, setModalData_editItem] = useState({
    showModal: false,
    bidItemId: null,
  });
  const toggleModal_editItem = () =>
    setModalData_editItem({
      ...modalData_editItem,
      showModal: !modalData_editItem.showModal,
    });

  const [modalData_dow, setModalData_dow] = useState({
    showModal: false,
    bidItemId: null,
  });
  const toggleModal_dow = () =>
    setModalData_dow({ ...modalData_dow, showModal: !modalData_dow.showModal });

  const accumulator = useCallback((accum, currVal) => accum + currVal, []);

  const getTotEeAllocated = useCallback((item) => {
    const iblEes = (item.locations || []).map(
      (ibl) => ibl.structureEeQuantity + ibl.roadwayEeQuantity
    );
    return iblEes.length >= 1 ? iblEes.reduce(accumulator) : 0;
  }, []);

  const getLocPercent = useCallback((ibl, item) => {
    const val = Math.round(
      ((ibl.structureEeQuantity + ibl.roadwayEeQuantity) / getTotEeAllocated(item)) * 100
    );
    return isNaN(val) ? 0 : val;
  }, []);

  const getTotLocPercent = useCallback((item) => {
    const iblPercents = (item.locations || []).map((ibl) => getLocPercent(ibl, item));
    return iblPercents.length >= 1 ? iblPercents.reduce(accumulator) : 0;
  }, []);

  const getStructureQuantityPercent = useCallback((item) => {
    // Location-based structure quantity (assigned via Items tab) takes priority over Division of Work's value
    const structureQty = item.totalStructureQuantity > 0 ? item.totalStructureQuantity : item.dowStructureQuantity;
    const val = Math.round((structureQty / item.eeQuantity) * 100);
    return (
      <span
        className={`${val > 0 ? "font-weight-bold" : ""}${
          val > 100 ? " text-danger" : ""
        }`}
      >
        {isNaN(val) ? 0 : val}%
      </span>
    );
  }, []);

  const data = useMemo(() => {
    // By desc OR number (based on number pattern matching)
    const filteredByDesc = descFilter
      ? (projItemList || []).filter((i) =>
          descFilter.match(/^\d{1,3}/)
            ? i.itemNo == descFilter
            : i.description.toLowerCase().includes(descFilter.trim().toLowerCase())
        )
      : projItemList;
    const filteredByPrimary =
      primaryFilter !== "filterAll"
        ? primaryFilter === "filterBridge"
          ? filteredByDesc.filter((i) => {
              // Location-based structure quantity (assigned via Items tab) takes priority over Division of Work's value
              const structureQty = i.totalStructureQuantity > 0 ? i.totalStructureQuantity : i.dowStructureQuantity;
              return structureQty > 0;
            })
          : filteredByDesc.filter((i) => {
              const structureQty = i.totalStructureQuantity > 0 ? i.totalStructureQuantity : i.dowStructureQuantity;
              return structureQty === 0;
            })
        : filteredByDesc;
    if (sortColumn.path !== "totPrice")
      return _.orderBy(filteredByPrimary, [sortColumn.path], [sortColumn.order]);
    else
      return _.orderBy(
        filteredByPrimary,
        (i) => (i.eeQuantity + i.ccoQuantity) * i.bidUnitPrice,
        [sortColumn.order]
      );
  }, [projItemList, sortColumn, primaryFilter, descFilter]);

  const refresh = () => {
    dispatch(bidItemActions.getAll(projectId));
    setExpandedItems({});
    setPrimaryFilter("filterAll");
    setDescFilter("");
  };

  // When expanding the row, fetch item details
  useEffect(() => {
    if (selectedBidItemId.current)
      dispatch(bidItemActions.get(selectedBidItemId.current, projectId));
  }, [selectedBidItemId.current]);

  const handleClick_item = (bidItemId) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    selectedBidItemId.current = bidItemId;
    setExpandedItems({ ...expandedItems, [bidItemId]: !expandedItems[bidItemId] });
  };

  return (
    <React.Fragment>
      <div className="p-2">
        <ProjAdminItemsListV2_SummaryBar
          projItemList={projItemList}
          accumulator={accumulator}
        />
        <ProjAdminItemsListV2_Filters
          primaryFilter={primaryFilter}
          descFilter={descFilter}
          setPrimaryFilter={setPrimaryFilter}
          setDescFilter={setDescFilter}
          showAddItemModal={() =>
            setModalData_editItem({ showModal: true, bidItemId: null })
          }
          refresh={refresh}
          projDetails={projDetails}
          permissions={permissions}
        />
        <div className="d-flex flex-row w-100">
          {/* <div className="alert alert-info p-2 w-100 mb-2">
            <FontAwesomeIcon icon="info-circle" className="fa-fw align-middle mr-2" />
            Locations can be assigned to items from below table. Click/tap on an item to
            expand, then go to "Edit Location Quantities...".
          </div> */}
        </div>
        <div className="item-list__table border">
          <div
            className="d-flex flex-row w-100 bg-lightgray item-list__table-header"
            style={{ zIndex: 9 }}
          >
            <div className="p-1 item-list__table-col--expand"></div>
            <div className="p-1 item-list__table-col--no">
              <b>No.</b>
            </div>
            <div className="p-1 item-list__table-col--desc">
              <b>Description</b>
            </div>
            <div className="p-1 item-list__table-col--code">
              <b>Code</b>
            </div>
            <div className="p-1 item-list__table-col--ee-qty text-right">
              <b>Bid Qty (EE)</b>
            </div>
            <div className="p-1 item-list__table-col--auth-qty text-right">
              <b>Auth. Qty</b>
            </div>
            <div className="p-1 item-list__table-col--uom">
              <b>Unit</b>
            </div>
            <div className="p-1 item-list__table-col--up text-right">
              <b>Unit Price</b>
            </div>
            <div className="p-1 item-list__table-col--tot-bid-amount text-right">
              <b>Bid Amount</b>
            </div>
            <div className="p-1 item-list__table-col--str-percent text-right">
              <b>Str. %</b>
            </div>
          </div>
          <div className="item-list__table-body">
            {data &&
              data.length > 0 &&
              data.map((bi, index) => (
                <div
                  key={`itemSummaryRowKey_${index}`}
                  className={classNames("dow-edit-row", {
                    "dow-selected": expandedItems[bi.bidItemId] || false,
                  })}
                  id="item-list__item-row"
                >
                  <div
                    className="d-flex flex-row item-list__table-row"
                    onClick={handleClick_item(bi.bidItemId)}
                  >
                    <div className="p-1 item-list__table-col--expand">
                      <FontAwesomeIcon
                        icon={
                          expandedItems[bi.bidItemId] || false
                            ? "angle-down"
                            : "angle-right"
                        }
                        className="fa-fw mx-1 align-middle"
                        style={{ fontSize: "1.2rem" }}
                      />
                    </div>
                    <div className="p-1 item-list__table-col--no">
                      <span
                        className={`align-middle ${
                          expandedItems[bi.bidItemId] || false ? "font-weight-bold" : ""
                        }`}
                      >
                        <small>#</small>
                        {bi.itemNo}
                      </span>
                    </div>
                    <div className="p-1 item-list__table-col--desc">
                      <span
                        className={`align-middle ${
                          expandedItems[bi.bidItemId] || false ? "font-weight-bold" : ""
                        }`}
                      >
                        {bi.description}
                      </span>
                    </div>
                    <div className="p-1 item-list__table-col--code">
                      {bi.code || "(N/A)"}
                    </div>
                    <div className="p-1 item-list__table-col--ee-qty text-right">
                      {formatThousands(bi.eeQuantity, 3)}
                    </div>
                    <div className="p-1 item-list__table-col--auth-qty text-right">
                      {formatThousands(bi.eeQuantity + bi.ccoQuantity, 3)}
                    </div>
                    <div className="p-1 item-list__table-col--uom">
                      {bi.unitOfMeasure}
                    </div>
                    <div className="p-1 item-list__table-col--up text-right">
                      {formatCurrency(bi.bidUnitPrice)}
                    </div>
                    <div className="p-1 item-list__table-col--tot-bid-amount text-right">
                      {formatCurrency((bi.eeQuantity + bi.ccoQuantity) * bi.bidUnitPrice)}
                    </div>
                    <div className="p-1 item-list__table-col--str-percent text-right">
                      {getStructureQuantityPercent(bi)}
                    </div>
                  </div>
                  <Collapse
                    isOpen={
                      (openBidItemDetails &&
                        openBidItemDetails[bi.bidItemId] &&
                        expandedItems[bi.bidItemId]) ||
                      false
                    }
                  >
                    {(expandedItems[bi.bidItemId] || false) &&
                      openBidItemDetails &&
                      openBidItemDetails[bi.bidItemId] && (
                        <ProjAdminItemsListV2_ExpandedDetails
                          setSelectedBidItemId={setSelectedBidItemId}
                          bidItemDetails={openBidItemDetails[bi.bidItemId]}
                          projectId={projectId}
                          getTotEeAllocated={getTotEeAllocated}
                          getLocPercent={getLocPercent}
                          getTotLocPercent={getTotLocPercent}
                          accumulator={accumulator}
                          showDowModal={() =>
                            setModalData_dow({
                              showModal: true,
                              bidItemId: selectedBidItemId.current,
                            })
                          }
                          showEditItemModal={() =>
                            setModalData_editItem({
                              showModal: true,
                              bidItemId: selectedBidItemId.current,
                            })
                          }
                          fetching={fetching}
                          permissions={permissions}
                        />
                      )}
                  </Collapse>
                </div>
              ))}
            {(!data || data.length === 0) && (
              <div
                key={`itemSummaryRowKey_empty`}
                className="dow-edit-row"
                id="item-list__item-row"
              >
                <div className="d-flex flex-row w-100 item-list__table-row">
                  {fetching ? `Loading...` : `(No Data)`}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {modalData_editItem.showModal && (
        <EditItemModalV2
          showModal={modalData_editItem.showModal}
          toggleModal={toggleModal_editItem}
          projectId={projectId}
          bidItemId={modalData_editItem.bidItemId}
        />
      )}
      {modalData_dow.showModal && (
        <EditDivOfWorkModalV3
          showModal={modalData_dow.showModal}
          toggleModal={toggleModal_dow}
          projectId={projectId}
          bidItemId={modalData_dow.bidItemId}
        />
      )}
    </React.Fragment>
  );
}

function ProjAdminItemsListV2_SummaryBar(props) {
  const { projItemList, accumulator } = props;

  const totBidAmt = useMemo(
    () =>
      projItemList && projItemList.length >= 1
        ? projItemList.map((i) => i.eeQuantity * i.bidUnitPrice).reduce(accumulator)
        : 0,
    [projItemList]
  );
  const totStrAmt = useMemo(
    () =>
      projItemList && projItemList.length >= 1
        ? projItemList
            .map((i) => {
              // Location-based structure quantity (assigned via Items tab) takes priority over Division of Work's value
              const structureQty = i.totalStructureQuantity > 0 ? i.totalStructureQuantity : i.dowStructureQuantity;
              return structureQty * i.bidUnitPrice;
            })
            .reduce(accumulator)
        : 0,
    [projItemList]
  );

  const totRdwyAmt = totBidAmt - totStrAmt;
  const strPercent = totBidAmt > 0 ? Math.round((totStrAmt / totBidAmt) * 100) : 0;

  return (
    <div className="card bg-lightgray mb-2">
      <div className="card-body p-2">
        <div className="d-flex flex-row flex-wrap w-100">
          <div className="item-list-summary__item mr-4">
            <strong>Total Bid:</strong> {formatCurrency(totBidAmt)}
          </div>
          <div className="item-list-summary__item mr-4">
            <strong>Structures:</strong> {formatCurrency(totStrAmt)} ({strPercent}%)
          </div>
          <div className="item-list-summary__item mr-4">
            <strong>Roadway:</strong> {formatCurrency(totRdwyAmt)} ({100 - strPercent}%)
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjAdminItemsListV2_Filters(props) {
  const {
    projDetails,
    fetching,
    primaryFilter,
    descFilter,
    setPrimaryFilter,
    setDescFilter,
    showAddItemModal,
    refresh,
    permissions,
  } = props;

  const handleClick_refresh = (e) => {
    e.preventDefault();
    refresh();
  };

  const handleClick_addItem = (e) => {
    e.preventDefault();
    showAddItemModal();
  };

  return (
    <div className="d-flex flex-row flex-wrap w-100 mb-1 pa-filter-group">
      <ButtonGroup className="pa-filter-item pa-filter-item__btngroup--item-type mr-2">
        <Button
          name="filterAll"
          id="filterAll"
          color="success"
          outline
          onClick={(e) => setPrimaryFilter(e.target.name)}
          active={primaryFilter === "filterAll"}
        >
          All
        </Button>
        <Button
          name="filterRoadway"
          id="filterRoadway"
          color="success"
          outline
          onClick={(e) => setPrimaryFilter(e.target.name)}
          active={primaryFilter === "filterRoadway"}
        >
          Roadway
        </Button>
        <Button
          name="filterBridge"
          id="filterBridge"
          color="success"
          outline
          onClick={(e) => setPrimaryFilter(e.target.name)}
          active={primaryFilter === "filterBridge"}
        >
          Structures
        </Button>
      </ButtonGroup>
      <div className="pa-filter-item pa-filter-item__input flex-grow-1 mr-2">
        <div className="input-group">
          <div className="input-group-prepend">
            <span className="input-group-text bg-lightgray">
              <FontAwesomeIcon icon="search" className="fa-fw text-muted" />
            </span>
          </div>
          <input
            name="descFilter"
            id="descFilter"
            className="form-control"
            value={descFilter}
            onChange={(e) => setDescFilter(e.target.value)}
            placeholder="Item # or Description"
          />
        </div>
      </div>
      <div className="pa-filter-item pa-filter-item__btn mr-2">
        <button
          type="button"
          name="refreshItemList"
          id="refreshItemList"
          className="btn btn-secondary"
          onClick={handleClick_refresh}
          disabled={fetching}
        >
          <FontAwesomeIcon icon="sync-alt" className="fa-fw mr-1" spin={fetching} />
          Refresh
        </button>
      </div>
      {projDetails && !["DBB_CT", "DBB_OTHER"].includes(projDetails.projectType) && (
        <div className="pa-filter-item pa-filter-item__btn">
          <button
            type="button"
            name="addItem"
            id="addItem"
            className="btn btn-info"
            onClick={handleClick_addItem}
            disabled={!permissions.includes(docPermConstants.EDIT_LEVEL_2)}
          >
            <FontAwesomeIcon icon="plus" className="fa-fw mr-1" />
            Add...
          </button>
        </div>
      )}
    </div>
  );
}

function ProjAdminItemsListV2_ExpandedDetails(props) {
  const {
    setSelectedBidItemId,
    bidItemDetails: item,
    projectId,
    accumulator,
    getTotEeAllocated,
    getLocPercent,
    getTotLocPercent,
    showEditItemModal,
    showDowModal,
    permissions,
    fetching,
  } = props;

  if (!item || fetching)
    return (
      <div className="card m-2 border border-primary">
        <div className="card-body p-2">
          <h6>Loading...</h6>
        </div>
      </div>
    );

  const projLocationList = useSelector(
    (state) => state.locations.projLocationLists[projectId] || null
  );
  const locationTypeList = useSelector(
    (state) => state.feederLists.locationTypeList || []
  );

  const getLocation = useCallback(
    (locationId) =>
      (projLocationList || []).find((l) => l.locationId === locationId) || null,
    [projLocationList]
  );
  const getLocationType = useCallback(
    (locTypeId) => locationTypeList.find((lt) => lt.locationTypeId === locTypeId) || null,
    [locationTypeList]
  );

  const totStrEeQty = useMemo(() => {
    const strEeQtys = (item.locations || []).map((ibl) => ibl.structureEeQuantity);
    return strEeQtys.length >= 1 ? +strEeQtys.reduce(accumulator).toFixed(3) : 0;
  }, [item]);

  const totStrCcoQty = useMemo(() => {
    const strCcoQtys = (item.locations || []).map((ibl) => ibl.structureCcoQuantity);
    return strCcoQtys.length >= 1 ? strCcoQtys.reduce(accumulator) : 0;
  }, [item]);

  const totRdwyEeQty = useMemo(() => {
    const rdwyEeQtys = (item.locations || []).map((ibl) => ibl.roadwayEeQuantity);
    return rdwyEeQtys.length >= 1 ? +rdwyEeQtys.reduce(accumulator).toFixed(3) : 0;
  }, [item]);

  const totRdwyCcoQty = useMemo(() => {
    const rdwyCcoQtys = (item.locations || []).map((ibl) => ibl.roadwayCcoQuantity);
    return rdwyCcoQtys.length >= 1 ? rdwyCcoQtys.reduce(accumulator) : 0;
  }, [item]);

  const totAllocQty = useMemo(() => getTotEeAllocated(item), [item]);

  const handleClick_editDow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedBidItemId(item.bidItemId);
    showDowModal();
  };

  const handleClick_editItem = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedBidItemId(item.bidItemId);
    showEditItemModal();
  };

  return (
    <React.Fragment>
      <div className="card m-2 border border-primary">
        <div className="card-body p-2">
          <h5 className="text-muted my-0 pb-2">Locations for This Item</h5>
          <div className="d-flex flex-row border-top border-bottom w-100 bg-lightgray">
            <div className="px-1 py-0 item-sub-list__table-col--loc border-right">
              <b>Location</b>
            </div>
            <div className="px-1 py-0 item-sub-list__table-col--loc-type border-right">
              <b>Loc. Type</b>
            </div>
            <div className="px-1 py-0 item-sub-list__table-col--rdwy-qty text-right border-right">
              <b>EE (Rdwy)</b>
            </div>
            <div className="px-1 py-0 item-sub-list__table-col--str-qty text-right border-right">
              <b>EE (Str)</b>
            </div>
            <div className="px-1 py-0 item-sub-list__table-col--rdwy-qty text-right border-right">
              <b>CO (Rdwy)</b>
            </div>
            <div className="px-1 py-0 item-sub-list__table-col--str-qty text-right border-right">
              <b>CO (Str)</b>
            </div>
            <div className="px-1 py-0 item-sub-list__table-col--portion-data text-right border-right">
              <b>% Alloc.</b>
            </div>
            <div className="px-1 py-0 item-sub-list__table-col--loc-amount text-right">
              <b>Loc. Subtotal</b>
            </div>
          </div>
          <div className="item-sub-list__table-body">
            {item.locations && item.locations.length > 0 ? (
              <React.Fragment>
                {item.locations.map((ibl) => {
                  const loc = getLocation(ibl.locationId);
                  const locType = loc ? getLocationType(loc.locationTypeId) : null;
                  return (
                    <div
                      key={`iblKey_${item.itemNo}_${ibl.locationId}`}
                      className="d-flex flex-row item-sub-list__table-row align-items-center border-bottom"
                    >
                      <div className="py-0 px-1 item-sub-list__table-col--loc border-right">
                        <span className="align-middle">
                          {loc ? loc.name : "(Loc. Name N/A)"}
                        </span>
                      </div>
                      <div className="py-0 px-1 item-sub-list__table-col--loc-type border-right">
                        <span className="align-middle">
                          {locType ? locType.description : "(Loc. Type N/A)"}
                        </span>
                      </div>
                      <div className="py-0 px-1 item-sub-list__table-col--str-qty text-right border-right">
                        <span className="align-middle">
                          {`${formatThousands(ibl.roadwayEeQuantity) || 0} ${
                            item.unitOfMeasure
                          }`}
                        </span>
                      </div>
                      <div className="py-0 px-1 item-sub-list__table-col--rdwy-qty text-right border-right">
                        <span className="align-middle">
                          {`${formatThousands(ibl.structureEeQuantity) || 0} ${
                            item.unitOfMeasure
                          }`}
                        </span>
                      </div>
                      <div className="py-0 px-1 item-sub-list__table-col--str-qty text-right border-right">
                        <span className="align-middle">
                          {`${formatThousands(ibl.roadwayCcoQuantity) || 0} ${
                            item.unitOfMeasure
                          }`}
                        </span>
                      </div>
                      <div className="py-0 px-1 item-sub-list__table-col--rdwy-qty text-right border-right">
                        <span className="align-middle">
                          {`${formatThousands(ibl.structureCcoQuantity) || 0} ${
                            item.unitOfMeasure
                          }`}
                        </span>
                      </div>
                      <div className="py-0 px-1 item-sub-list__table-col--portion-data text-right border-right">
                        {`${getLocPercent(ibl, item)}%`}
                      </div>
                      <div className="py-0 px-1 item-sub-list__table-col--loc-amount text-right">
                        {formatCurrency(
                          (ibl.structureEeQuantity +
                            ibl.structureCcoQuantity +
                            ibl.roadwayEeQuantity +
                            ibl.roadwayCcoQuantity) *
                            item.bidUnitPrice
                        )}
                      </div>
                    </div>
                  );
                })}
                <div
                  key={`iblKey_${item.itemNo}_iblTotal`}
                  className="d-flex flex-row item-sub-list__table-row align-items-center border-bottom"
                >
                  <div className="px-1 py-0 item-sub-list__table-col--loc"></div>
                  <div className="px-1 py-0 item-sub-list__table-col--loc-type border-right text-right">
                    <b>Total =</b>
                  </div>
                  <div className="px-1 py-0 item-sub-list__table-col--rdwy-qty text-right border-right">
                    <b>
                      {formatThousands(totRdwyEeQty)} {item.unitOfMeasure}
                    </b>
                  </div>
                  <div className="px-1 py-0 item-sub-list__table-col--str-qty text-right border-right">
                    <b>
                      {formatThousands(totStrEeQty)} {item.unitOfMeasure}
                    </b>
                  </div>
                  <div className="px-1 py-0 item-sub-list__table-col--rdwy-qty text-right border-right">
                    <b>
                      {formatThousands(totRdwyCcoQty)} {item.unitOfMeasure}
                    </b>
                  </div>
                  <div className="px-1 py-0 item-sub-list__table-col--str-qty text-right border-right">
                    <b>
                      {formatThousands(totStrCcoQty)} {item.unitOfMeasure}
                    </b>
                  </div>
                  <div className="px-1 py-0 item-sub-list__table-col--portion-data text-right border-right">
                    <b>{getTotLocPercent(item)}%</b>
                  </div>
                  <div className="px-1 py-0 item-sub-list__table-col--loc-amount text-right">
                    <span
                      className={`font-weight-bold ${
                        totAllocQty > item.eeQuantity ? " text-danger" : ""
                      }`}
                    >
                      {formatCurrency(totAllocQty * item.bidUnitPrice)}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            ) : (
              <div className="d-flex flex-row item-sub-list__table-row ml-2 border-bottom">
                (Location subdivision does not exist for this item.)
              </div>
            )}
          </div>
          {totAllocQty > item.eeQuantity && (
            <div className="d-flex flex-row my-2">
              <div className="alert alert-warning p-2 w-100 mb-0">
                <h6>
                  <FontAwesomeIcon icon="exclamation-triangle" className="fa-fw mr-2" />
                  Quantity Overrun
                </h6>
                Sum of the location quantities exceed this item's contract bid quantity.
                Please review the breakdown.
              </div>
            </div>
          )}
          <div className="d-flex flex-row mt-2">
            <button
              type="button"
              name="editDow"
              id={`editDow_${item.bidItemId}`}
              className="btn btn-secondary btn-sm mr-2"
              onClick={handleClick_editDow}
              disabled={!permissions.includes(docPermConstants.EDIT_LEVEL_2)}
            >
              Edit Location Quantities...
            </button>
            <button
              name="editItem"
              id={`editItem_${item.bidItemId}`}
              className="btn btn-secondary btn-sm"
              onClick={handleClick_editItem}
              disabled={!permissions.includes(docPermConstants.EDIT_LEVEL_2)}
            >
              Edit Item...
            </button>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
